import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Logo } from "@/components/logo";
import { seoMeta } from "@/lib/seo/head";
import { Loader2, MailCheck } from "lucide-react";

export const Route = createFileRoute("/forgot-password")({
  head: () =>
    seoMeta({
      title: "Recupera password",
      description: "Reimposta la password del tuo account LexAIPro: ti invieremo un link sicuro via email per impostare una nuova password.",
      path: "/forgot-password",
      noindex: true,
    }),
  component: ForgotPasswordPage,
});

const GENERIC_MESSAGE =
  "Se esiste un account associato a questa email, riceverai a breve un link per reimpostare la password. Controlla anche la cartella spam.";

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  // honeypot
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);

  const remainingCooldown = cooldownUntil
    ? Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000))
    : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || remainingCooldown > 0) return;
    setLoading(true);
    try {
      const res = await fetch("/api/public/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          website,
          redirectTo: `${window.location.origin}/reset-password`,
        }),
      });
      if (res.status === 429) {
        toast.error(
          "Troppe richieste. Aspetta qualche minuto prima di riprovare.",
        );
        setCooldownUntil(Date.now() + 60_000);
        return;
      }
      // Always show the same generic confirmation
      setSent(true);
      setCooldownUntil(Date.now() + 60_000);
    } catch {
      toast.error("Errore di rete. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <div className="w-full max-w-md space-y-8">
        <div className="flex justify-center">
          <Logo variant="light" size={48} />
        </div>
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-serif">Recupera password</h1>
          <p className="text-muted-foreground">
            Inserisci la tua email: ti invieremo un link per impostare una nuova
            password.
          </p>
        </div>
        {sent ? (
          <div className="rounded-lg border bg-card p-6 text-center space-y-4">
            <div className="flex justify-center">
              <MailCheck className="h-10 w-10 text-primary" />
            </div>
            <p className="text-sm">{GENERIC_MESSAGE}</p>
            <div className="flex flex-col gap-2 text-sm">
              <Link to="/login" className="text-primary hover:underline">
                Torna al login
              </Link>
              <button
                type="button"
                onClick={() => {
                  if (remainingCooldown > 0) return;
                  setSent(false);
                }}
                className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                disabled={remainingCooldown > 0}
              >
                {remainingCooldown > 0
                  ? `Reinvia tra ${remainingCooldown}s`
                  : "Non l'hai ricevuta? Riprova"}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                disabled={loading}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            {/* honeypot - hidden from real users */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="hidden"
              aria-hidden="true"
            />
            <Button
              type="submit"
              className="w-full"
              disabled={loading || remainingCooldown > 0 || !email}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Invio in corso...
                </>
              ) : remainingCooldown > 0 ? (
                `Riprova tra ${remainingCooldown}s`
              ) : (
                "Invia link di recupero"
              )}
            </Button>
          </form>
        )}
        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="text-primary hover:underline">
            Torna al login
          </Link>
        </p>
      </div>
    </div>
  );
}
